import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/* -------------------------------------------------------------------------- */
/*  Primitives — shared tokens for every text-entry shell                     */
/* -------------------------------------------------------------------------- */

export const formControlTransition =
  "transition-[color,box-shadow,border-color]"

export const formControlPlaceholder = "placeholder:text-muted-foreground"

/** Placeholder styling for slot-based controls (Select value, etc.). */
export const formControlPlaceholderSlot =
  "data-placeholder:text-muted-foreground"

/** Destructive focus ring when `aria-invalid` is set. */
export const formControlInvalidFocusRing =
  "aria-invalid:focus-visible:ring-destructive-ring-translucent"

export const formControlFocusRing = cn(
  "focus-visible:ring-2",
  "not-aria-invalid:focus-visible:ring-ring/50",
  formControlInvalidFocusRing
)

export const formControlFocusDefault = cn(
  "focus-visible:border-primary",
  formControlFocusRing
)

/** Open/focused shell state (Autocomplete dropdown open, etc.). */
export const formControlFocusOpen = cn(
  "border-primary ring-2",
  "not-aria-invalid:ring-ring/50",
  "aria-invalid:ring-destructive-ring-translucent"
)

/* -------------------------------------------------------------------------- */
/*  Shell + interaction states                                                */
/* -------------------------------------------------------------------------- */

export const formControlShell = cn(
  "rounded-md border bg-background",
  "text-foreground",
  formControlPlaceholder,
  formControlTransition,
  "outline-none",
  formControlFocusRing
)

export const formControlDisabled = cn(
  "disabled:pointer-events-none disabled:cursor-not-allowed",
  "disabled:bg-muted disabled:text-muted-foreground",
  "disabled:placeholder:text-muted-foreground"
)

/** Native `readOnly` attribute styles — only for `<input>` / `<textarea>`. */
export const formControlReadOnly = cn(
  "read-only:cursor-default read-only:bg-muted",
  "read-only:text-foreground",
  "read-only:placeholder:text-muted-foreground"
)

export const formControlDataReadOnly = cn(
  "data-readonly:cursor-default",
  "data-readonly:bg-muted data-readonly:text-foreground"
)

export const formControlInvalid = cn(
  "aria-invalid:border-destructive",
  "aria-invalid:focus-visible:border-destructive"
)

/** Border hover for button/div shells (Select, Autocomplete) and native inputs. */
export const formControlShellHover = cn(
  "hover:border-primary",
  "read-only:hover:border-border",
  "data-readonly:hover:border-border",
  "disabled:hover:border-border",
  "data-disabled:hover:border-border"
)

/**
 * Shared base for form-control shells (inputs, triggers, combobox shells).
 * Does not include `read-only:` utilities — those only apply correctly on
 * native text controls; use `formControlReadOnly` there, `formControlDataReadOnly`
 * on button triggers, and `formControlReadOnlyShell` on programmatic shells.
 */
export const formControlFieldBase = cn(
  formControlShell,
  formControlDisabled,
  formControlInvalid
)

export const formControlVariantDefault = cn(
  "border-input",
  formControlShellHover,
  "disabled:border-border",
  "read-only:border-border",
  "data-readonly:border-border data-disabled:border-border",
  formControlFocusDefault
)

/** Ghost for buttons / div shells — `data-readonly` only (not CSS `:read-only`). */
export const formControlVariantGhostShell = cn(
  "border-transparent bg-transparent shadow-none",
  "hover:border-transparent",
  "focus-visible:ring-0",
  "disabled:border-transparent disabled:bg-transparent",
  "data-readonly:border-transparent data-readonly:bg-transparent",
  "data-disabled:border-transparent data-disabled:bg-transparent"
)

/** Ghost for native text controls — includes `read-only:` for the attribute. */
export const formControlVariantGhostInput = cn(
  formControlVariantGhostShell,
  "read-only:border-transparent read-only:bg-transparent"
)

export const formControlVariantSuccess = cn(
  "border-success",
  "hover:border-success",
  "disabled:border-success",
  "read-only:border-success read-only:hover:border-success",
  "data-readonly:border-success data-readonly:hover:border-success",
  "data-disabled:border-success data-disabled:hover:border-success",
  "focus-visible:border-success",
  formControlFocusRing
)

export const formControlVariantWarning = cn(
  "border-warning",
  "hover:border-warning",
  "disabled:border-warning",
  "read-only:border-warning read-only:hover:border-warning",
  "data-readonly:border-warning data-readonly:hover:border-warning",
  "data-disabled:border-warning data-disabled:hover:border-warning",
  "focus-visible:border-warning",
  formControlFocusRing
)

export const formControlVariantError = cn(
  "border-destructive",
  "hover:border-destructive",
  "disabled:border-destructive",
  "read-only:border-destructive read-only:hover:border-destructive",
  "data-readonly:border-destructive data-readonly:hover:border-destructive",
  "data-disabled:border-destructive data-disabled:hover:border-destructive",
  "focus-visible:border-destructive",
  formControlFocusRing
)

const formControlVariantMapCore = {
  default: formControlVariantDefault,
  success: formControlVariantSuccess,
  warning: formControlVariantWarning,
  error: formControlVariantError,
} as const

/** Border-based validation variants for button/div shells (Select, Autocomplete). */
export const formControlVariantMap = {
  ...formControlVariantMapCore,
  ghost: formControlVariantGhostShell,
} as const satisfies Record<FormControlVariant, string>

/** Border-based validation variants for native text controls (Input, Textarea). */
export const formControlInputVariantMap = {
  ...formControlVariantMapCore,
  ghost: formControlVariantGhostInput,
} as const satisfies Record<FormControlVariant, string>

export const formControlDisabledShell = cn(
  "pointer-events-none cursor-not-allowed",
  "bg-muted text-muted-foreground"
)

export const formControlReadOnlyShell = cn(
  "cursor-default bg-muted text-foreground"
)

export type FormControlVariant =
  | "default"
  | "ghost"
  | "success"
  | "warning"
  | "error"

/** Ring-based validation variants for file inputs. */
export const formControlFileVariantDefault = cn(
  "ring-1 ring-input",
  "hover:ring-primary",
  formControlFocusRing,
  "file:bg-primary file:text-primary-foreground"
)

export const formControlFileVariantGhost = cn(
  formControlVariantGhostInput,
  "ring-0",
  "file:bg-transparent file:text-primary"
)

export const formControlFileVariantSuccess = cn(
  "ring-1 ring-success",
  formControlFocusRing,
  "file:bg-success file:text-success-foreground"
)

export const formControlFileVariantWarning = cn(
  "ring-1 ring-warning",
  formControlFocusRing,
  "file:bg-warning file:text-warning-foreground"
)

export const formControlFileVariantError = cn(
  "ring-1 ring-destructive",
  formControlFocusRing,
  "file:bg-destructive file:text-destructive-foreground"
)

export const formControlFileVariantMap = {
  default: formControlFileVariantDefault,
  ghost: formControlFileVariantGhost,
  success: formControlFileVariantSuccess,
  warning: formControlFileVariantWarning,
  error: formControlFileVariantError,
} as const satisfies Record<FormControlVariant, string>

/** Programmatic shell states for div-based controls (e.g. Autocomplete). */
export const formControlShellStateVariants = cva("", {
  variants: {
    open: {
      true: formControlFocusOpen,
      false: "",
    },
    disabled: {
      true: formControlDisabledShell,
      false: "",
    },
    readOnly: {
      true: formControlReadOnlyShell,
      false: "",
    },
  },
  defaultVariants: {
    open: false,
    disabled: false,
    readOnly: false,
  },
})

export type FormControlShellStateVariants = VariantProps<
  typeof formControlShellStateVariants
>

/* -------------------------------------------------------------------------- */
/*  Toggle / field helpers (non text-entry)                                   */
/* -------------------------------------------------------------------------- */

export const formControlToggleDisabled = cn(
  "data-disabled:pointer-events-none data-disabled:cursor-not-allowed",
  "data-disabled:opacity-disabled-content",
  "data-disabled:focus-visible:ring-0"
)

export const formControlFieldDisabled =
  "group-has-disabled/field:opacity-disabled-content group-data-[disabled=true]/field:opacity-disabled-content"

/** Shared focus ring for toggle controls (checkbox, radio, switch, slider thumb). */
export const formControlToggleFocus = cn("outline-none", formControlFocusRing)

/** Focus ring for slider thumbs — focus lives on the nested range input. */
export const formControlSliderFocus = cn(
  "outline-none",
  "has-[:focus-visible]:ring-2",
  "group-not-aria-invalid/slider:has-[:focus-visible]:ring-ring/50",
  "group-aria-invalid/slider:has-[:focus-visible]:ring-destructive-ring-translucent"
)

export const formControlToggleInvalid = cn(
  "aria-invalid:border-destructive",
  "aria-invalid:data-checked:border-destructive",
  "aria-invalid:data-indeterminate:border-destructive"
)

export const formControlSliderInvalid = cn(
  "group-aria-invalid/slider:border-destructive"
)
